import { pb } from "@/lib/pocketbase";
import { pbRepository } from "./pocketbase-repository";
import { auth } from "./auth.service";
import type { ExamType, Topic, TopicState } from "@/types/models";

const repo = pbRepository<Topic>("topics");
const TOPIC_STATES: TopicState[] = ["active", "done", "archived"];

function normalizeState(value: unknown): TopicState {
  const raw = Array.isArray(value) ? value[0] : value;
  return TOPIC_STATES.includes(raw as TopicState) ? (raw as TopicState) : "active";
}

/**
 * Leitet den Status automatisch aus dem Prüfungstermin ab. Es gibt genau
 * drei: „archived" bleibt immer von Hand erhalten, ein vergangener Termin
 * macht das Thema „done", alles andere ist „active".
 */
export function computeAutoState(examDate: string | undefined, stored: TopicState): TopicState {
  if (stored === "archived") return "archived";
  if (!examDate) return "active";
  const d = new Date(examDate);
  if (isNaN(d.getTime())) return "active";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const days = Math.round((target.getTime() - today.getTime()) / 86400000);
  return days < 0 ? "done" : "active";
}

function normalizeTopic(topic: Topic): Topic {
  const stored = normalizeState((topic as { state?: unknown }).state);
  return {
    ...topic,
    state: computeAutoState(topic.examDate, stored),
  };
}

/**
 * Laufende Themen eines Fachs: weder archiviert noch mit vergangenem
 * Prüfungstermin. Nur zwischen diesen lohnt eine Auswahl (Hauptthema, Thema
 * einer neuen Hausaufgabe).
 */
export function openTopics(topics: Topic[]): Topic[] {
  return topics.filter((t) => t.state === "active");
}

/**
 * Das „aktuelle" Thema eines Fachs – die eine Definition, auf die sich
 * Startseite und Stundenplan gleichermaßen stützen: das gesetzte Hauptthema,
 * sonst das laufende, sonst das nächste anstehende, sonst das erste nicht
 * archivierte.
 */
export function pickCurrentTopic(topics: Topic[], mainTopicId?: string): Topic | undefined {
  const open = topics.filter((t) => t.state !== "archived");
  const main = mainTopicId
    ? open.find((t) => t.id === mainTopicId && t.state !== "done")
    : undefined;
  return main ?? open.find((t) => t.state === "active") ?? open[0];
}

export const topicsService = {
  /** Das aktuelle Thema eines Fachs (siehe `pickCurrentTopic`). */
  async currentBySubject(subjectId: string): Promise<Topic | undefined> {
    const [topics, subject] = await Promise.all([
      this.listBySubject(subjectId),
      pb
        .collection("subjects")
        .getOne<{ mainTopic?: string }>(subjectId)
        .catch(() => undefined),
    ]);
    return pickCurrentTopic(topics, subject?.mainTopic);
  },

  listBySubject(subjectId: string): Promise<Topic[]> {
    return repo
      .list({
        filter: pb.filter("subject = {:s}", { s: subjectId }),
        sort: "created",
      })
      .then((items) => items.map(normalizeTopic));
  },

  listAll(): Promise<Topic[]> {
    return repo.list({ sort: "-created" }).then((items) => items.map(normalizeTopic));
  },

  get(id: string): Promise<Topic> {
    return repo.getOne(id).then(normalizeTopic);
  },

  create(data: {
    subject: string;
    title: string;
    description?: string;
    state?: TopicState;
    examDate?: string;
    examType?: ExamType | "";
    examPeriodFrom?: number;
    examPeriodTo?: number;
  }): Promise<Topic> {
    return repo.create({
      ...data,
      state: data.state ?? "active",
      owner: auth.currentUser()?.id,
    } as Partial<Topic>).then(normalizeTopic);
  },

  update(id: string, data: Partial<Topic>): Promise<Topic> {
    return repo.update(id, data).then(normalizeTopic);
  },

  remove(id: string): Promise<void> {
    return repo.remove(id);
  },

  /** Termin und Prüfungsform des Themas in einem Rutsch setzen (leer = kein Termin). */
  setExam(
    id: string,
    exam: { date: string; examType: ExamType | ""; periodFrom?: number; periodTo?: number }
  ): Promise<Topic> {
    return repo
      .update(id, {
        examDate: exam.date || "",
        examType: exam.date ? exam.examType : "",
        examPeriodFrom: exam.periodFrom ?? null,
        examPeriodTo: exam.periodTo ?? null,
      } as unknown as Partial<Topic>)
      .then(normalizeTopic);
  },

  /**
   * Einmalige, stille Rückmigration: Solange Prüfungstermine in einer eigenen
   * `exams`-Collection lagen, konnte ein Thema mehrere haben. Jetzt trägt das
   * Thema wieder genau einen – übernommen wird der nächste anstehende, sonst
   * der zuletzt vergangene. Läuft höchstens einmal pro Gerät und bricht still
   * ab, wenn es die Collection nicht (mehr) gibt.
   */
  async migrateExamsToTopicFields(): Promise<void> {
    const FLAG = "lernapp.examsMigrated";
    try {
      if (localStorage.getItem(FLAG) === "1") return;
    } catch {
      return;
    }
    try {
      const exams = await pb
        .collection("exams")
        .getFullList<LegacyExam>({ sort: "date", requestKey: null });
      const byTopic = new Map<string, LegacyExam[]>();
      for (const exam of exams) {
        const list = byTopic.get(exam.topic);
        if (list) list.push(exam);
        else byTopic.set(exam.topic, [exam]);
      }
      for (const [topicId, list] of byTopic) {
        const pick = nearestLegacyExam(list);
        if (!pick) continue;
        await repo
          .update(topicId, {
            examDate: pick.date,
            examType: pick.examType,
            examPeriodFrom: pick.periodFrom ?? null,
            examPeriodTo: pick.periodTo ?? null,
          } as unknown as Partial<Topic>)
          .catch(() => undefined);
      }
    } catch {
      // Collection nicht vorhanden oder nicht lesbar – nichts zu tun.
    }
    try {
      localStorage.setItem(FLAG, "1");
    } catch {
      /* ignore */
    }
  },
};

interface LegacyExam {
  topic: string;
  date: string;
  examType: ExamType;
  periodFrom?: number;
  periodTo?: number;
}

function nearestLegacyExam(exams: LegacyExam[]): LegacyExam | undefined {
  if (!exams.length) return undefined;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const at = (e: LegacyExam) => new Date(e.date).getTime();
  const upcoming = exams.filter((e) => at(e) >= today.getTime()).sort((a, b) => at(a) - at(b));
  return upcoming[0] ?? [...exams].sort((a, b) => at(b) - at(a))[0];
}
